import {PureComponent} from "preact/compat";
import CharacterSelect from "./character_select";
import {setTimeoutWithVisibility, TimeoutHandle} from "../../misc/visibilitytimeout";
import {CharacterTrainingProgress, CharacterNameAndId, request_manager} from "../../server";
import {Settings} from "../md";

interface HeaderProps {
    characters: CharacterNameAndId[];
    selected: number | null;
    on_character_select: (id: number, name: string, valid: boolean) => void;
    on_toggle_settings: () => void;
}

interface HeaderState {
    training_data: {[id: number]: CharacterTrainingProgress};
    current_time: Date;
}

export default class Header extends PureComponent<HeaderProps, HeaderState> {
    earliest_end_date: Date | undefined;
    timeout: TimeoutHandle | undefined;
    hovered: boolean;

    constructor(props) {
        super(props);
        const training_data: {[id: number]: CharacterTrainingProgress} = {};
        for (const character of props.characters) {
            if (!character.valid) {
                training_data[character.id] = {
                    character_id: character.id,
                    level: undefined,
                    sp: undefined,
                    skill_id: undefined,
                    start_date: undefined,
                    end_date: undefined,
                };
            }
        }
        this.state = {training_data, current_time: new Date()};
        this.earliest_end_date = undefined;
        this.timeout = undefined;
        this.hovered = false;
    }

    async componentDidMount() {
        await this.update_training_data();
    }

    async update_training_data() {
        let training_data = {...this.state.training_data};
        const now = new Date();
        for (const [char_id, item] of Object.entries(training_data)) {
            if (item !== undefined && item.end_date !== undefined && item.end_date <= now) {
                training_data[char_id] = undefined;
            }
        }

        this.setState({training_data, current_time: now});
        this.earliest_end_date = new Date();
        this.earliest_end_date.setMinutes(this.earliest_end_date.getMinutes() + 10);
        try {
            for await (const progress of request_manager.download_character_training_progress()) {
                if (
                    progress.end_date != undefined &&
                    (this.earliest_end_date === undefined ||
                        progress.end_date < this.earliest_end_date)
                ) {
                    this.earliest_end_date = progress.end_date;
                }
                training_data = {...training_data};
                training_data[progress.character_id] = progress;
                this.setState({training_data, current_time: new Date()});
            }
        } catch {
            // NeedsLoginError: handled elsewhere. This endpoint always returns 200, so
            // CharacterNeedsUpdated can't happen here.
        }
        this.update_time();
    }

    on_mouse_enter = () => {
        this.hovered = true;
        this.update_time();
    };

    on_mouse_leave = () => {
        this.hovered = false;
    };

    update_time() {
        if (this.timeout !== undefined) {
            this.timeout.cancel();
        }
        const now = +new Date();
        let delay = this.hovered ? 1000 : 10000;
        if (this.earliest_end_date !== undefined) {
            const earliest = +this.earliest_end_date;
            if (now >= earliest) {
                this.update_training_data();
                return;
            }
            delay = Math.min(earliest - now, delay);
        }
        this.timeout = setTimeoutWithVisibility(() => {
            this.timeout = undefined;
            this.update_time();
        }, delay);
        this.setState({current_time: new Date()});
    }

    render() {
        return (
            <header onMouseEnter={this.on_mouse_enter} onMouseLeave={this.on_mouse_leave}>
                <div>
                    {(this.props.characters || []).map((c) => (
                        <CharacterSelect
                            id={c.id}
                            key={c.id}
                            valid={c.valid}
                            name={c.name}
                            current_time={this.state.current_time}
                            selected={c.id == this.props.selected}
                            on_click={this.props.on_character_select}
                            training={this.state.training_data[c.id]}
                        />
                    ))}
                    <div className="spacer" />
                    <div onClick={this.props.on_toggle_settings} className="header_settings">
                        <Settings />
                    </div>
                </div>
            </header>
        );
    }
}
